import { BusinessObjectBase, decodeDecimals } from './base';
import { supportCaseSchema } from '@schemas/business-objects/support-case';
import type { Account } from './account';
import type { SupportAttachment } from './support-attachment';
import type { SupportComment } from './support-comment';
import type { User } from './user';
import type {
  Id,
  Markdown,
  SupportCasePriority,
  SupportCaseStatus,
} from '../base-types';

export interface SupportCaseData {
  readonly id: number;
  readonly title?: string;
  readonly description?: Markdown;
  readonly status?: SupportCaseStatus;
  readonly priority?: SupportCasePriority;
  readonly accountId: number;
  readonly account?: Account;
  readonly comments?: SupportComment[];
  readonly attachments?: SupportAttachment[];
  readonly createdAt?: Date;
  readonly createdById?: Id;
  readonly updatedAt?: Date;
  readonly updatedById?: Id;
  readonly createdBy?: User;
  readonly updatedBy?: User;
}

export interface SupportCase extends Readonly<SupportCaseData> {}
type SupportCaseCreateWithChildrenData = Partial<
  Omit<SupportCaseData, 'comments' | 'attachments'>
> & {
  comments?: Partial<SupportComment>[];
  attachments?: Partial<SupportAttachment>[];
};

export class SupportCase extends BusinessObjectBase {
  static readonly entityName = 'SupportCase' as const;
  static readonly plural = 'support-cases';
  static schema = supportCaseSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<SupportCase[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new SupportCase(data));
  }

  static async findOne(filter?: any): Promise<SupportCase | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new SupportCase(data) : null;
  }

  static async findById(id: number, filter?: any): Promise<SupportCase> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new SupportCase(data);
  }

  static async create(data: Omit<SupportCaseData, 'id'>): Promise<SupportCase> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new SupportCase(result);
  }

  static async createMany(
    data: Omit<SupportCaseData, 'id'>[],
  ): Promise<SupportCase[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new SupportCase(item));
  }

  static async createWithChildren(
    data: SupportCaseCreateWithChildrenData,
  ): Promise<SupportCase> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new SupportCase(result);
  }

  static async createManyWithChildren(
    data: SupportCaseCreateWithChildrenData[],
  ): Promise<SupportCase[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new SupportCase(item));
  }

  static async updateById(
    id: number,
    updates: Partial<SupportCaseData>,
  ): Promise<SupportCase> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new SupportCase(result);
  }

  static async deleteById(id: number): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  static async findOneWithProfile(filter: any): Promise<SupportCase | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one-with-profile`;
    return this.post<SupportCase | null>(url, filter).then((value) =>
      decodeDecimals(supportCaseSchema, value),
    );
  }
  static async findWithProfile(filter: any): Promise<SupportCase[]> {
    const url = `${this.baseUrl}/${this.plural}/find-with-profile`;
    return this.post<SupportCase[]>(url, filter).then((values) =>
      values?.map((value) => decodeDecimals(supportCaseSchema, value)),
    );
  }
}
